/*
 * London Attitude customisations. Loaded after theme.js.
 */

/* Tap-to-copy discount code chip (snippets/la-copy-code.liquid) */
if (!customElements.get('la-copy-code')) {
  customElements.define('la-copy-code', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('button')?.addEventListener('click', this._copy.bind(this));
    }

    async _copy() {
      const hint = this.querySelector('.la-copy-code__hint');

      try {
        await navigator.clipboard.writeText(this.dataset.code);
      } catch (error) {
        // Older or stricter browsers: fall back to a temporary selection
        const input = Object.assign(document.createElement('textarea'), { value: this.dataset.code });
        input.style.cssText = 'position:fixed;opacity:0';
        document.body.append(input);
        input.select();
        const copied = document.execCommand('copy');
        input.remove();

        if (!copied) {
          return; // The code is still visible to type manually
        }
      }

      if (hint) {
        hint.dataset.originalText ??= hint.textContent;
        hint.textContent = hint.dataset.copiedText;
        clearTimeout(this._timer);
        this._timer = setTimeout(() => hint.textContent = hint.dataset.originalText, 2000);
      }
    }
  });
}

/* Rotating offers on mobile for the two-tier promo bar (sections/la-promo-bar.liquid) */
if (!customElements.get('la-promo-bar-offers')) {
  customElements.define('la-promo-bar-offers', class extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.children);
      this.index = 0;

      if (this.items.length < 2) {
        return;
      }

      this.media = window.matchMedia('(max-width: 699px)');
      this._tick = this._tick.bind(this);
      this._sync = this._sync.bind(this);
      this.media.addEventListener('change', this._sync);
      this._sync();
    }

    disconnectedCallback() {
      clearInterval(this._interval);
      this.media?.removeEventListener('change', this._sync);
    }

    _sync() {
      clearInterval(this._interval);
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === this.index));

      if (this.media.matches) {
        this._interval = setInterval(this._tick, (parseInt(this.getAttribute('speed')) || 4) * 1000);
      }
    }

    _tick() {
      this.index = (this.index + 1) % this.items.length;
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === this.index));
    }
  });
}

/* "Read more" for clamped collection descriptions (sections/collection-banner.liquid) */
if (!customElements.get('la-read-more')) {
  customElements.define('la-read-more', class extends HTMLElement {
    connectedCallback() {
      const content = this.querySelector('.la-read-more__content');
      const toggle = this.querySelector('.la-read-more__toggle');

      if (!content || !toggle) {
        return;
      }

      // Only offer the toggle when the text is actually cut off
      requestAnimationFrame(() => {
        toggle.hidden = content.scrollHeight <= content.clientHeight + 1;
      });

      toggle.dataset.moreText = toggle.textContent;
      toggle.addEventListener('click', () => {
        const expanded = this.classList.toggle('is-expanded');
        toggle.textContent = expanded ? toggle.dataset.lessText : toggle.dataset.moreText;
      });
    }
  });
}

/* Filter dropdowns in a row (sections/main-collection.liquid): one open at a time, close on outside click / Escape */
const closeFacetDropdowns = (except) => {
  document.querySelectorAll('.la-facets-bar accordion-disclosure').forEach((dropdown) => {
    if (dropdown !== except && dropdown.querySelector('details')?.getAttribute('aria-expanded') === 'true') {
      dropdown.close();
    }
  });
};

document.addEventListener('click', (event) => {
  closeFacetDropdowns(event.target.closest('.la-facets-bar accordion-disclosure'));
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    closeFacetDropdowns();
  }
});

/* Collapsible footer menus on mobile (sections/footer.liquid) */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('.la-footer-toggle');

  if (!toggle || !window.matchMedia('(max-width: 699px)').matches) {
    return;
  }

  const expanded = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!expanded));
});

/* Keep a footer panel open when it holds a form message (e.g. "Thanks for subscribing") */
document.querySelectorAll('.la-footer-panel:has(.banner)').forEach((panel) => {
  document.querySelector(`[aria-controls="${panel.id}"]`)?.setAttribute('aria-expanded', 'true');
});

/* Scroll indicator under product carousels on mobile, so shoppers can tell there is more to swipe */
const setupScrollProgress = (root = document) => {
  root.querySelectorAll('.product-list--carousel').forEach((carousel) => {
    const container = carousel.closest('product-list') || carousel;

    if (container.nextElementSibling?.classList.contains('la-scroll-progress')) {
      return;
    }

    const track = document.createElement('div');
    track.className = 'la-scroll-progress';
    track.setAttribute('aria-hidden', 'true');
    track.innerHTML = '<span class="la-scroll-progress__thumb"></span>';
    container.after(track);

    const thumb = track.firstElementChild;
    const update = () => {
      const scrollable = carousel.scrollWidth - carousel.clientWidth;
      track.hidden = scrollable <= 1;

      // Keep the thumb at least 20% wide so it stays visible on long carousels
      const ratio = Math.max(carousel.clientWidth / carousel.scrollWidth, 0.2);
      thumb.style.width = `${ratio * 100}%`;
      thumb.style.transform = `translateX(${(carousel.scrollLeft / Math.max(scrollable, 1)) * ((1 - ratio) / ratio) * 100}%)`;
    };

    carousel.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  });
};

setupScrollProgress();
document.addEventListener('shopify:section:load', (event) => setupScrollProgress(event.target));

/* Product gallery arrows and "3 / 9" image counter (snippets/product-gallery.liquid) */
if (!customElements.get('la-gallery-counter')) {
  customElements.define('la-gallery-counter', class extends HTMLElement {
    async connectedCallback() {
      const carousel = document.getElementById(this.getAttribute('aria-controls'));

      if (!carousel) {
        return;
      }

      await customElements.whenDefined(carousel.localName);

      const stage = this.closest('.la-gallery-stage');

      const update = (index = carousel.selectedIndex ?? 0) => {
        const total = carousel.cells?.length ?? 0;
        this.hidden = total < 2;
        this.textContent = `${Math.min(index + 1, total)} / ${total}`;

        // Hide the arrow at each end
        const prev = stage?.querySelector('[data-la-gallery="previous"]');
        const next = stage?.querySelector('[data-la-gallery="next"]');
        if (prev) prev.disabled = index <= 0;
        if (next) next.disabled = index >= total - 1;
      };

      stage?.addEventListener('click', (event) => {
        const action = event.target.closest('[data-la-gallery]')?.dataset.laGallery;

        if (action === 'previous') carousel.previous();
        if (action === 'next') carousel.next();
      });

      carousel.addEventListener('carousel:change', (event) => update(event.detail.index));
      carousel.addEventListener('carousel:filter', () => requestAnimationFrame(() => update()));
      update();
    }
  });
}

/* Expose the promo bar's height so the homepage banner can fit exactly in the first screen */
const promoBar = document.querySelector('.la-promo-bar');

if (promoBar) {
  const setPromoBarHeight = () => document.documentElement.style.setProperty('--la-promo-bar-height', `${promoBar.offsetHeight}px`);
  new ResizeObserver(setPromoBarHeight).observe(promoBar);
  setPromoBarHeight();
}
